/**
 * Cấu hình tab Futures Control — cùng shape với SaleControlPage (`SalesControlTabConfig`).
 * accessForm / evidence / actions bám plan Access (`DocConvert_Future_overview`, form_frmFutures*).
 */
export type FuturesTabKey =
  | 'summary'
  | 'margin-report'
  | 'market'
  | 'allocation'
  | 'orders'
  | 'spreads'
  | 'pucontract'
  | 'local-s'
  | 'bank-cash-in'
  | 'bank-statement'
  | 'adjust-lot'
  | 'bank-cost-change'
  | 'cash-balance'
  | 'position'
  | 'pnl'
  | 'check-futures'
  | 'reconciliation'

export interface FuturesControlTabConfig {
  key: FuturesTabKey
  label: string
  accessLabel: string
  accessForm: string
  evidence: string[]
  actions: string[]
  description: string
  /**
   * `main` — các nút nav dọc trên `frmFuturesMain` (TabIndex 0–11).
   * `summary-extended` — báo cáo mở từ nhánh Summary (evidence frmFuturesSummary_*).
   */
  navGroup: 'main' | 'summary-extended'
}

export const FUTURES_TAB_CONFIG: FuturesControlTabConfig[] = [
  {
    key: 'summary',
    label: 'Summary',
    accessLabel: 'SUMMARY',
    accessForm: 'frmFuturesMainSubFormReportSum',
    evidence: [
      'form_frmFuturesMainSubFormReportSum.txt',
      'form_frmFuturesRpSummary.txt',
      'form_frmFuturesQryRpSummary*.txt',
      'report_rpFuturesSummary.txt',
    ],
    actions: [],
    description: 'Default landing page summarizing the futures dashboard.',
    navGroup: 'main',
  },
  {
    key: 'margin-report',
    label: 'Margin Report',
    accessLabel: 'Margin Report',
    accessForm: 'frmFuturesMainSubFormReport → rpFuturesMargin',
    evidence: [
      'form_frmFuturesMainSubFormReport.txt',
      'form_frmFuturesRpMargin.txt',
      'form_frmFuturesQryReportMarginChange.txt',
      'report_rpFuturesMargin.txt',
    ],
    actions: [],
    description: 'Embedded margin report.',
    navGroup: 'main',
  },
  {
    key: 'market',
    label: 'Market',
    accessLabel: 'Market',
    accessForm: 'frmFuturesMainSubFormMarket → frmFuturesQryMarket',
    evidence: [
      'form_frmFuturesMainSubFormMarket.txt',
      'form_frmFuturesQryMarket.txt',
      'query_FuturesQryMarket.txt',
    ],
    actions: [],
    description: 'Market price reference per exchange and contract.',
    navGroup: 'main',
  },
  {
    key: 'allocation',
    label: 'Allocation',
    accessLabel: 'Allocation',
    accessForm: 'frmFuturesMainSubFormAllocation → frmFuturesQryAllocation',
    evidence: [
      'form_frmFuturesMainSubFormAllocation.txt',
      'form_frmFuturesQryAllocation.txt',
      'query_FuturesQryAllocation.txt',
    ],
    actions: ['New', 'Edit', 'Delete', 'Check - No Alc'],
    description: 'Futures allocation list.',
    navGroup: 'main',
  },
  {
    key: 'orders',
    label: 'Futures',
    accessLabel: 'Futures',
    accessForm: 'frmFuturesMainSubFormFuturesOrder → frmFuturesQryFuturesOrder',
    evidence: [
      'form_frmFuturesMainSubFormFuturesOrder.txt',
      'form_frmFuturesQryFuturesOrder.txt',
      'query_FuturesQryFuturesOrder.txt',
    ],
    actions: ['New', 'Edit', 'Delete', 'Mass Upload'],
    description: 'Futures orders list.',
    navGroup: 'main',
  },
  {
    key: 'spreads',
    label: 'Spreads',
    accessLabel: 'Spreads',
    accessForm: 'frmFuturesMainSubFormSpreads → frmFuturesQrySpreads',
    evidence: [
      'form_frmFuturesMainSubFormSpreads.txt',
      'form_frmFuturesQrySpreads.txt',
      'query_FuturesQrySpreads.txt',
    ],
    actions: ['New', 'Edit', 'Delete', 'Mass Upload'],
    description: 'Spread orders list.',
    navGroup: 'main',
  },
  {
    key: 'pucontract',
    label: 'Purchase',
    accessLabel: 'Local P',
    accessForm: 'frmFuturesMain → frmFuturesQryLocalP',
    evidence: ['form_frmFuturesMain.txt', 'form_frmFuturesQryLocalP.txt', 'query_FuturesQryLocalP.txt'],
    actions: [],
    description: 'Local purchase contracts linked to futures.',
    navGroup: 'main',
  },
  {
    key: 'local-s',
    label: 'Sales',
    accessLabel: 'Local S',
    accessForm: 'frmFuturesMain → frmFuturesQryLocalS',
    evidence: ['form_frmFuturesMain.txt', 'form_frmFuturesQryLocalS.txt', 'query_FuturesQryLocalS.txt'],
    actions: [],
    description: 'Local sales contracts.',
    navGroup: 'main',
  },
  {
    key: 'bank-cash-in',
    label: 'Cash-in',
    accessLabel: 'Cash-in',
    accessForm: 'frmFuturesMainSubFormBank → frmFuturesQryBankCashIn*',
    evidence: [
      'form_frmFuturesMainSubFormBank.txt',
      'form_frmFuturesQryBankCashIn.txt',
      'query_FuturesQryBankCashIn.txt',
    ],
    actions: ['New', 'Edit', 'Delete'],
    description: 'Cash deposits/withdrawals by bank (VTB / MBB / BIDV).',
    navGroup: 'main',
  },
  {
    key: 'bank-statement',
    label: 'Bank Statement',
    accessLabel: 'Bank Statement',
    accessForm: 'frmFuturesMainSubFormBank → frmFuturesQryBankStatementVTB/MBB/BIDV',
    evidence: [
      'form_frmFuturesMainSubFormBank.txt',
      'form_frmFuturesQryBankStatementVTB.txt',
      'query_FuturesQryBankStatement.txt',
    ],
    actions: ['New', 'Edit', 'Delete'],
    description: 'Bank statements — list and add separately per bank.',
    navGroup: 'main',
  },
  {
    key: 'adjust-lot',
    label: 'Adjustment',
    accessLabel: 'Adjustment',
    accessForm: 'frmFuturesMainSubForm → frmFuturesQryAdjustLot',
    evidence: [
      'form_frmFuturesMainSubForm.txt',
      'form_frmFuturesQryAdjustLot.txt',
      'query_FuturesQryAdjustLot.txt',
    ],
    actions: ['New', 'Edit', 'Delete'],
    description: 'Lot adjustments.',
    navGroup: 'main',
  },
  {
    key: 'bank-cost-change',
    label: 'Bank cost change',
    accessLabel: 'Bank cost change',
    accessForm: 'frmFuturesMainSubForm → frmFuturesQryBankCostChange',
    evidence: [
      'form_frmFuturesMainSubForm.txt',
      'form_frmFuturesQryBankCostChange.txt',
      'query_FuturesQryBankCostChange.txt',
    ],
    actions: ['New', 'Edit', 'Delete'],
    description: 'Bank fee changes.',
    navGroup: 'main',
  },
  {
    key: 'cash-balance',
    label: 'Cash Balance',
    accessLabel: 'Cash Balance',
    accessForm: 'frmFuturesSummary_CashBalance_*',
    evidence: [
      'form_frmFuturesSummary_CashBalance_Vietinbank.txt',
      'form_frmFuturesSummary_CashBalance_MBBank.txt',
      'form_frmFuturesSummary_CashBalance_BIDV.txt',
    ],
    actions: [],
    description: 'Cash balance by bank (Summary sub-report).',
    navGroup: 'summary-extended',
  },
  {
    key: 'position',
    label: 'Position',
    accessLabel: 'Position',
    accessForm: 'frmFuturesSummary_Position*',
    evidence: [
      'form_frmFuturesSummary_Position.txt',
      'form_frmFuturesSummary_Position_All.txt',
      'form_frmFuturesQryRpSummaryPos.txt',
    ],
    actions: [],
    description: 'Position report.',
    navGroup: 'summary-extended',
  },
  {
    key: 'pnl',
    label: 'PnL',
    accessLabel: 'PnL',
    accessForm: 'frmFuturesSummary_PnL',
    evidence: ['form_frmFuturesSummary_PnL.txt', 'report_rpFuturesSummary.txt'],
    actions: [],
    description: 'Profit and loss summary.',
    navGroup: 'summary-extended',
  },
  {
    key: 'check-futures',
    label: 'Check Futures',
    accessLabel: 'Check Futures',
    accessForm: 'frmFuturesSummary_CheckFutures*',
    evidence: [
      'form_frmFuturesSummary_CheckFutures.txt',
      'form_frmFuturesSummary_CheckFutures_LD.txt',
      'form_frmFuturesSummary_CheckFutures_NY.txt',
    ],
    actions: [],
    description: 'Futures verification (LD / NY variants).',
    navGroup: 'summary-extended',
  },
  {
    key: 'reconciliation',
    label: 'Reconciliation',
    accessLabel: 'Reconciliation',
    accessForm: 'frmFuturesSummary_Reconcillation_*',
    evidence: [
      'form_frmFuturesSummary_Reconcillation_Vietinbank.txt',
      'form_frmFuturesSummary_Reconcillation_MBBank.txt',
      'form_frmFuturesSummary_Reconcillation_BIDV.txt',
    ],
    actions: [],
    description: 'Reconciliation between books and bank statements.',
    navGroup: 'summary-extended',
  },
]

export function isFuturesTabKey(value: string | undefined): value is FuturesTabKey {
  return FUTURES_TAB_CONFIG.some((tab) => tab.key === value)
}

/** Nav dọc `frmFuturesMain` (SUMMARY → Bank cost change) — khớp thứ tự Access. */
export const FUTURES_TAB_NAV_MAIN = FUTURES_TAB_CONFIG.filter((t) => t.navGroup === 'main')

/** Báo cáo nhánh Summary (evidence frmFuturesSummary_*) — không phải 12 nút nav chính. */
export const FUTURES_TAB_NAV_SUMMARY_EXT = FUTURES_TAB_CONFIG.filter(
  (t) => t.navGroup === 'summary-extended',
)
